import { useCallback, useEffect, useMemo, useState } from 'react';

import { FaArrowLeft, FaPenToSquare, FaPlay, FaTrashCan, FaWandMagicSparkles } from 'react-icons/fa6';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { useApp } from 'common/AppContext';
import { Markup } from 'common/Markup';

import { VibeEditModal } from './VibeEditModal';
import { unwrap } from './helpers';

const PRIORITY_VARIANT = {
    1: 'secondary',
    2: 'info',
    3: 'warning',
    4: 'danger'
};

/**
 * Render one step entry, with shared-step refs expanded inline. The
 * shared-steps map is `testCase.shared_steps_resolved` populated by the
 * backend on case fetch; missing entries fall back to a placeholder so
 * the user knows a shared step exists but couldn't be loaded.
 */
function renderSeparatedEntry(s, sharedMap) {

    if (s.shared_step_id) {

        const shared = sharedMap && sharedMap[s.shared_step_id];
        const subSteps = shared && Array.isArray(shared.custom_steps_separated)
            ? shared.custom_steps_separated
            : [];
        return (
            <div className="border rounded p-2" style={{ background: 'rgba(13,110,253,0.05)' }}>
                <div className="d-flex align-items-center gap-2 mb-1">
                    <Badge bg="info">
                        Shared
                    </Badge>
                    <strong className="small">
                        {shared?.title || `Shared step #${s.shared_step_id}`}
                    </strong>
                    <span className="text-muted small">
                        #
                        {s.shared_step_id}
                    </span>
                </div>
                {subSteps.length ? (
                    <ol className="mb-0 small">
                        {subSteps.map((ss, j) => (
                            <li key={j}>
                                <Markup placeholder="(empty)">
                                    {ss.content}
                                </Markup>
                                {ss.expected && (
                                    <div className="text-muted">
                                        <em>
                                            Expected:
                                        </em>
                                        {' '}
                                        <Markup inline>
                                            {ss.expected}
                                        </Markup>
                                    </div>
                                )}
                            </li>
                        ))}
                    </ol>
                ) : (
                    <em className="text-muted small">
                        {shared ? '(shared step has no sub-steps)' : '(could not load shared step)'}
                    </em>
                )}
            </div>
        );

    }

    return (
        <>
            <Markup placeholder="(empty)">
                {s.content}
            </Markup>
            {s.expected && (
                <div className="text-muted small mt-1">
                    <strong>
                        Expected:
                    </strong>
                    {' '}
                    <Markup inline>
                        {s.expected}
                    </Markup>
                </div>
            )}
        </>
    );

}

/**
 * Render the case's steps for display. TestRail can return either
 * `custom_steps` (one big text block) or `custom_steps_separated`
 * (array of {content, expected} pairs, with shared-step references
 * mixed in by id).
 */
function renderStepsView(testCase) {

    if (Array.isArray(testCase.custom_steps_separated) && testCase.custom_steps_separated.length) {

        const sharedMap = testCase.shared_steps_resolved || {};
        return (
            <ol className="mb-0">
                {testCase.custom_steps_separated.map((s, i) => (
                    <li className="mb-2" key={i}>
                        {renderSeparatedEntry(s, sharedMap)}
                    </li>
                ))}
            </ol>
        );

    }

    if (testCase.custom_steps && testCase.custom_steps.trim()) {

        return (
            <Markup>
                {testCase.custom_steps}
            </Markup>
        );

    }

    return (
        <em className="text-muted">
            No steps
        </em>
    );

}

/**
 * Flatten a case's steps to plain text so it can be sent to the runner as
 * a freeform brief without an AI plan-generation hop. Shared step refs
 * are expanded inline using shared_steps_resolved (provided by backend);
 * each shared block is preceded by a "[shared:" header so the runner
 * knows where one shared sequence begins and ends.
 */
function flattenStepsToText(testCase) {

    if (Array.isArray(testCase.custom_steps_separated) && testCase.custom_steps_separated.length) {

        const sharedMap = testCase.shared_steps_resolved || {};
        return testCase.custom_steps_separated.map((s, i) => {

            if (s.shared_step_id) {

                const shared = sharedMap[s.shared_step_id];
                if (shared && Array.isArray(shared.custom_steps_separated) && shared.custom_steps_separated.length) {

                    const subs = shared.custom_steps_separated.map((ss, j) => {

                        const action = (ss.content || '').trim();
                        const expected = (ss.expected || '').trim();
                        return `   ${i + 1}.${j + 1}. ${action}${expected ? ` (expected: ${expected})` : ''}`;

                    }).join('\n');
                    return `${i + 1}. [shared: ${shared.title || `#${s.shared_step_id}`}]\n${subs}`;

                }
                return `${i + 1}. [shared step #${s.shared_step_id} — unavailable]`;

            }

            const action = (s.content || '').trim();
            const expected = (s.expected || '').trim();
            return `${i + 1}. ${action}${expected ? `\n   Expected: ${expected}` : ''}`;

        }).join('\n');

    }
    return (testCase.custom_steps || '').trim();

}

export const CaseDetail = () => {

    const { api } = useApp();
    const { id } = useParams();
    const history = useHistory();

    const [testCase, setTestCase] = useState(null);
    const [caseTypes, setCaseTypes] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({});
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [vibeOpen, setVibeOpen] = useState(false);

    // AI-plan + launch panel
    const [planning, setPlanning] = useState(false);
    const [plan, setPlan] = useState(null);
    const [planError, setPlanError] = useState(null);
    const [editedSteps, setEditedSteps] = useState('');
    const [selectedEnvUrl, setSelectedEnvUrl] = useState('');

    const load = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const data = await api.get(`/api/testrail/cases/${id}`);
            setTestCase(data);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setLoading(false);

        }

    }, [api, id]);

    useEffect(() => {

        load();

    }, [load]);

    useEffect(() => {

        api.get('/api/testrail/case-types').then(d => setCaseTypes(unwrap(d, 'case_types'))).catch(() => {});
        api.get('/api/testrail/priorities').then(d => setPriorities(unwrap(d, 'priorities'))).catch(() => {});

    }, [api]);

    const typeName = useMemo(
        () => Object.fromEntries(caseTypes.map(t => [t.id, t.name])),
        [caseTypes]
    );
    const priorityName = useMemo(
        () => Object.fromEntries(priorities.map(p => [p.id, p.name])),
        [priorities]
    );

    const startEdit = () => {

        setDraft({
            custom_preconds: testCase.custom_preconds || '',
            custom_steps: testCase.custom_steps || '',
            priority_id: testCase.priority_id || '',
            refs: testCase.refs || '',
            title: testCase.title || '',
            type_id: testCase.type_id || ''
        });
        setEditing(true);

    };

    const saveEdit = async () => {

        setSaving(true);
        setError(null);

        try {

            const body = {
                custom_preconds: draft.custom_preconds,
                custom_steps: draft.custom_steps,
                refs: draft.refs,
                title: draft.title
            };
            if (draft.type_id) body.type_id = parseInt(draft.type_id, 10);
            if (draft.priority_id) body.priority_id = parseInt(draft.priority_id, 10);

            const updated = await api.request('PUT', `/api/testrail/cases/${id}`, body);
            setTestCase(updated);
            setEditing(false);

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setSaving(false);

        }

    };

    const remove = async () => {

        if (!window.confirm('Delete this test case from TestRail? This cannot be undone.')) return;

        setDeleting(true);
        setError(null);

        try {

            await api.request('DELETE', `/api/testrail/cases/${id}`);
            history.push('/testrail/cases');

        } catch (e) {

            setError(e?.err || e?.message || String(e));
            setDeleting(false);

        }

    };

    const generatePlan = async () => {

        setPlanning(true);
        setPlanError(null);

        try {

            const data = await api.post(`/api/testrail/cases/${id}/plan`);
            setPlan(data);
            setEditedSteps((data.steps || []).join('\n'));
            setSelectedEnvUrl(data.envs && data.envs.length ? data.envs[0].url : '');

        } catch (e) {

            setPlanError(e?.err || e?.message || String(e));

        } finally {

            setPlanning(false);

        }

    };

    const launch = mode => {

        // mode: 'ai' (use the AI-generated steps + matched env) or 'literal'
        // (use the case's own steps verbatim — needs an env picked manually)
        let testPlan, url, appId;

        if (mode === 'ai' && plan) {

            const lines = editedSteps.split('\n').map(s => s.trim()).filter(Boolean);
            if (!lines.length || !selectedEnvUrl) return;
            testPlan = lines.join('\n');
            url = selectedEnvUrl;
            appId = plan.app ? plan.app.id : null;

        } else if (mode === 'literal') {

            testPlan = `${testCase.title}\n\n${flattenStepsToText(testCase)}`.trim();
            url = selectedEnvUrl;
            appId = null;

        } else {

            return;

        }

        history.push('/feature-tester', {
            prefill: {
                appId,
                freeform: true,
                source: { id: testCase.id, title: testCase.title, type: 'testrail_case' },
                testPlan,
                url
            }
        });

    };

    if (loading) return (
        <Container className="py-4">
            <Spinner animation="border" />
        </Container>
    );

    if (error && !testCase)
        return (
            <Container className="py-4">
                <Button className="mb-3" onClick={() => history.push('/testrail/cases')} variant="outline-secondary">
                    <FaArrowLeft className="me-2" />
                    Back to cases
                </Button>
                <Alert variant="danger">
                    {error}
                </Alert>
            </Container>
        );

    return (

        <Container className="py-4">

            <Button className="mb-3" onClick={() => history.push('/testrail/cases')} variant="outline-secondary">
                <FaArrowLeft className="me-2" />
                Back to cases
            </Button>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            <Row className="g-3">

                <Col lg={7}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>

                            {!editing ? (
                                <>
                                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                                        <div>
                                            <div className="text-muted small">
                                                C
                                                {testCase.id}
                                            </div>
                                            <h4 className="mb-1">
                                                {testCase.title}
                                            </h4>
                                            <div className="d-flex gap-2">
                                                {testCase.type_id && (
                                                    <Badge bg="secondary">
                                                        {typeName[testCase.type_id] || `Type #${testCase.type_id}`}
                                                    </Badge>
                                                )}
                                                {testCase.priority_id && (
                                                    <Badge bg={PRIORITY_VARIANT[testCase.priority_id] || 'secondary'}>
                                                        {priorityName[testCase.priority_id] || `P${testCase.priority_id}`}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button onClick={() => setVibeOpen(true)} size="sm" variant="outline-primary">
                                                <FaWandMagicSparkles className="me-1" />
                                                {' '}
                                                Vibe edit
                                            </Button>
                                            <Button onClick={startEdit} size="sm" variant="outline-secondary">
                                                <FaPenToSquare className="me-1" />
                                                {' '}
                                                Edit
                                            </Button>
                                            <Button disabled={deleting} onClick={remove} size="sm" variant="outline-danger">
                                                <FaTrashCan />
                                            </Button>
                                        </div>
                                    </div>

                                    {testCase.refs && (
                                        <div className="text-muted small mb-3">
                                            <strong>
                                                Refs:
                                            </strong>
                                            {' '}
                                            {testCase.refs}
                                        </div>
                                    )}

                                    {testCase.custom_preconds && testCase.custom_preconds.trim() && (
                                        <>
                                            <h6 className="text-uppercase text-muted small mt-3 mb-2" style={{ letterSpacing: 0.5 }}>
                                                Preconditions
                                            </h6>
                                            <Markup>
                                                {testCase.custom_preconds}
                                            </Markup>
                                        </>
                                    )}

                                    <h6 className="text-uppercase text-muted small mt-3 mb-2" style={{ letterSpacing: 0.5 }}>
                                        Steps
                                    </h6>
                                    {renderStepsView(testCase)}
                                </>
                            ) : (
                                <Form>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Title
                                        </Form.Label>
                                        <Form.Control
                                            onChange={e => setDraft(d => ({ ...d, title: e.target.value }))}
                                            value={draft.title || ''}
                                        />
                                    </Form.Group>
                                    <Row className="g-2 mb-3">
                                        <Col md={6}>
                                            <Form.Label>
                                                Type
                                            </Form.Label>
                                            <Form.Select
                                                onChange={e => setDraft(d => ({ ...d, type_id: e.target.value }))}
                                                value={draft.type_id || ''}
                                            >
                                                <option value="">
                                                    —
                                                </option>
                                                {caseTypes.map(t => (
                                                    <option key={t.id} value={t.id}>
                                                        {t.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Label>
                                                Priority
                                            </Form.Label>
                                            <Form.Select
                                                onChange={e => setDraft(d => ({ ...d, priority_id: e.target.value }))}
                                                value={draft.priority_id || ''}
                                            >
                                                <option value="">
                                                    —
                                                </option>
                                                {priorities.map(p => (
                                                    <option key={p.id} value={p.id}>
                                                        {p.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Col>
                                    </Row>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Refs
                                        </Form.Label>
                                        <Form.Control
                                            onChange={e => setDraft(d => ({ ...d, refs: e.target.value }))}
                                            placeholder="Comma-separated"
                                            value={draft.refs || ''}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Preconditions
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            onChange={e => setDraft(d => ({ ...d, custom_preconds: e.target.value }))}
                                            rows={3}
                                            value={draft.custom_preconds || ''}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Steps
                                            <span className="text-muted small ms-2">
                                                (monolithic format — separated step/expected pairs not editable here)
                                            </span>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            onChange={e => setDraft(d => ({ ...d, custom_steps: e.target.value }))}
                                            rows={10}
                                            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                            value={draft.custom_steps || ''}
                                        />
                                    </Form.Group>
                                    <div className="d-flex gap-2 justify-content-end">
                                        <Button disabled={saving} onClick={() => setEditing(false)} variant="outline-secondary">
                                            Cancel
                                        </Button>
                                        <Button disabled={saving || !draft.title?.trim()} onClick={saveEdit} variant="primary">
                                            {saving ? 'Saving…' : 'Save changes'}
                                        </Button>
                                    </div>
                                </Form>
                            )}

                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={5}>
                    <Card className="border-0 shadow-sm">
                        <Card.Body>

                            <div className="d-flex align-items-center gap-2 mb-2">
                                <FaWandMagicSparkles className="text-primary" />
                                <h5 className="m-0">
                                    Run in Feature Tester
                                </h5>
                            </div>

                            {!plan ? (
                                <>
                                    <p className="text-muted small">
                                        Generate a tester-ready summary, mapped target app, and 3-8 actionable steps.
                                        You can edit them before launching.
                                    </p>
                                    {planError && (
                                        <Alert variant="danger">
                                            {planError}
                                        </Alert>
                                    )}
                                    <Button disabled={planning} onClick={generatePlan} variant="primary">
                                        {planning ? (
                                            <>
                                                <Spinner animation="border" as="span" className="me-2" size="sm" />
                                                Analyzing…
                                            </>
) : 'Generate AI test plan'}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    {plan.summary && (
                                        <div className="mb-3 p-2 rounded border" style={{ background: 'rgba(0,0,0,0.02)' }}>
                                            <div className="text-muted small mb-1">
                                                Summary
                                            </div>
                                            <div className="small" style={{ whiteSpace: 'pre-wrap' }}>
                                                {plan.summary}
                                            </div>
                                        </div>
                                    )}

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Environment
                                            {plan.app && (
                                                <span className="text-muted small ms-2">
                                                    (matched to
                                                    {' '}
                                                    {plan.app.name}
                                                    )
                                                </span>
                                            )}
                                        </Form.Label>
                                        {plan.envs && plan.envs.length > 0 ? (
                                            <Form.Select
                                                onChange={e => setSelectedEnvUrl(e.target.value)}
                                                value={selectedEnvUrl}
                                            >
                                                {plan.envs.map(env => (
                                                    <option key={env.id} value={env.url}>
                                                        {env.name}
                                                        {' '}
                                                        —
                                                        {env.url}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        ) : (
                                            <Alert className="mb-0" variant="warning">
                                                No environment matched. Pick one manually:
                                                <Form.Control
                                                    className="mt-2"
                                                    onChange={e => setSelectedEnvUrl(e.target.value)}
                                                    placeholder="https://dev.gmetrix.net"
                                                    type="url"
                                                    value={selectedEnvUrl}
                                                />
                                            </Alert>
                                        )}
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                        <Form.Label>
                                            Test steps
                                            <small className="text-muted ms-2">
                                                (one per line — edit freely)
                                            </small>
                                        </Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            onChange={e => setEditedSteps(e.target.value)}
                                            rows={10}
                                            style={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}
                                            value={editedSteps}
                                        />
                                    </Form.Group>

                                    <div className="d-flex gap-2 justify-content-between">
                                        <Button onClick={generatePlan} size="sm" variant="outline-secondary">
                                            Regenerate
                                        </Button>
                                        <Button
                                            disabled={!editedSteps.trim() || !selectedEnvUrl}
                                            onClick={() => launch('ai')}
                                            variant="success"
                                        >
                                            <FaPlay className="me-2" />
                                            Launch in Feature Tester
                                        </Button>
                                    </div>

                                    <hr />
                                    <div className="text-muted small">
                                        Or skip AI and run the case&apos;s own steps verbatim:
                                    </div>
                                    <Button
                                        className="mt-2"
                                        disabled={!selectedEnvUrl}
                                        onClick={() => launch('literal')}
                                        size="sm"
                                        variant="outline-secondary"
                                    >
                                        Launch with literal case steps
                                    </Button>
                                </>
                            )}

                        </Card.Body>
                    </Card>
                </Col>

            </Row>

            <VibeEditModal
                onApplied={updated => setTestCase(updated)}
                onHide={() => setVibeOpen(false)}
                show={vibeOpen}
                testCase={testCase}
            />

        </Container>

    );

};
